/**
 * IngredientsList Component
 * 
 * Displays the scrolling list of ingredients with detailed information.
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { INGREDIENTS, type Ingredient } from './ingredients.data';
import { IngredientModal } from './IngredientModal';

const AUTO_SCROLL_PX_PER_TICK = 1;
const AUTO_SCROLL_INTERVAL_MS = 40; // ≈ 25 px/sec — slow continuous drift
const RESUME_DELAY_MS = 2000;

export const IngredientsList: React.FC = () => {
  const [selectedIngredient, setSelectedIngredient] = useState<Ingredient | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isPausedRef = useRef(false);
  const resumeTimerRef = useRef<number | null>(null);

  const pauseAutoScroll = () => {
    isPausedRef.current = true;
    if (resumeTimerRef.current !== null) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  };

  const scheduleResume = () => {
    if (resumeTimerRef.current !== null) {
      window.clearTimeout(resumeTimerRef.current);
    }
    resumeTimerRef.current = window.setTimeout(() => {
      isPausedRef.current = false;
      resumeTimerRef.current = null;
    }, RESUME_DELAY_MS);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const tick = window.setInterval(() => {
      const el = scrollContainerRef.current;
      if (!el || isPausedRef.current || selectedIngredient) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      const next = el.scrollLeft >= max - 1 ? 0 : el.scrollLeft + AUTO_SCROLL_PX_PER_TICK;
      el.scrollTo({ left: next, behavior: 'auto' });
    }, AUTO_SCROLL_INTERVAL_MS);

    return () => {
      window.clearInterval(tick);
      if (resumeTimerRef.current !== null) window.clearTimeout(resumeTimerRef.current);
    };
  }, [selectedIngredient]);

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
      pauseAutoScroll();
      scheduleResume();
    }
  };

  const handleIngredientClick = (ingredient: Ingredient) => {
    setSelectedIngredient(ingredient);
  };

  return (
    <div className="relative isolate">
      {/* Scroll buttons - visible only on desktop/larger screens */}
      <button
        onClick={() => handleScroll('left')}
        className="absolute left-0 top-1/2 transform -translate-y-1/2 z-20 bg-black/80 p-2 rounded-full border border-[#ff00ff]/20 text-white/60 hover:text-white transition-colors hover:bg-black hidden md:block"
        aria-label="Scroll left"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      
      <button
        onClick={() => handleScroll('right')}
        className="absolute right-0 top-1/2 transform -translate-y-1/2 z-20 bg-black/80 p-2 rounded-full border border-[#ff00ff]/20 text-white/60 hover:text-white transition-colors hover:bg-black hidden md:block"
        aria-label="Scroll right"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Loading indicator for initial image preloading */}
      <div className="absolute top-4 right-4 z-30 bg-black/80 px-3 py-1 rounded-full border border-[#ff00ff]/20" style={{ display: 'none' }}>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-[#ff00ff] rounded-full animate-pulse" />
          <span className="text-white/80 text-sm">Loading images...</span>
        </div>
      </div>
    
      {/* Scrollable container - auto-scrolls slowly; pauses on user interaction */}
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto overscroll-x-contain ingredients-scroll-container !pointer-events-auto px-4 md:px-0 relative z-10"
        onMouseEnter={pauseAutoScroll}
        onMouseLeave={scheduleResume}
        onPointerDown={pauseAutoScroll}
        onPointerUp={scheduleResume}
        onTouchStart={pauseAutoScroll}
        onTouchEnd={scheduleResume}
        onWheel={() => { pauseAutoScroll(); scheduleResume(); }}
        onFocus={pauseAutoScroll}
        onBlur={scheduleResume}
      >
        <div className="flex gap-4 md:gap-6 py-4 md:py-8 md:px-12">
          {INGREDIENTS.map((ingredient, index) => (
            <div 
              key={index}
              className="flex-shrink-0 snap-center relative"
              role="button"
              tabIndex={0}
              aria-label={`View details for ${ingredient.name}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleIngredientClick(ingredient);
                }
              }}
            >
              <div 
                className="group relative glass-panel rounded-xl p-2 sm:p-4 md:p-6 border border-white/10 hover:border-[#ff00ff]/20 hover-glow cursor-pointer overflow-hidden h-[250px] md:h-[300px] w-[180px] md:w-[240px] transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center isolate"
                onClick={() => handleIngredientClick(ingredient)}
              >
                {/* Glow effect */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#ff00ff]/10 to-[#00ffff]/10 rounded-full blur-2xl transform translate-x-6 -translate-y-6 group-hover:scale-150 transition-transform duration-500 -z-10" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-[#ff00ff]/10 to-[#00ffff]/10 rounded-full blur-2xl transform -translate-x-6 translate-y-6 group-hover:scale-150 transition-transform duration-500 -z-10" />
                
                <div className="relative flex flex-col items-center justify-center sm:justify-start mt-0 text-center w-full h-full z-10">
                  {/* Powder image */}
                  <div className="relative flex items-center justify-center w-full h-full">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-black/10 to-purple-900/10 blur-md group-hover:opacity-70 transition-opacity duration-300 -z-10"></div>
                    
                    <img 
                      src={ingredient.image} 
                      alt={ingredient.name}
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_0_12px_rgba(255,0,255,0.4)]"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedIngredient && (
        <IngredientModal 
          ingredient={selectedIngredient} 
          onClose={() => setSelectedIngredient(null)} 
        />
      )}
    </div>
  );
};